import React, { useEffect, useState, useRef, useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'

// Components
import Icon from './icon/Icon'
import Button, { IProps as IButtonProps } from './Button'
import Link from './Link'
import { GridRow, GridCol } from './grid'
import Text from './Text'
import Avatar, { AvatarSize } from './Avatar'
import StepDots from './StepDots'
import { UserTags } from './card/UserDetails'
import { Row, RowAligned } from './layout/LayoutAtoms'
import GoalCount from './GoalCount'
import { ContextMenu, ISection } from './ContextButton'

// Styles
import { mqFrom, mqTo } from '../styles/mediaQueries'
import { shadows } from '../styles/effects'
import { applyTextStyle } from '../styles/typography'

// Types
import { IconType } from './icon/IconType'
import { ICompanyMember } from '../common/models/Company'

export interface IPath {
  name: string
  // In case the page doesn't have a landing page yet: should be visible in menu structure but not clickable
  route?: string
}

export interface INavTabs {
  path: string
  name: string
  title: string
  onClick?: () => void
  disabled?: boolean
  active?: boolean
  // A small red dot next to the label — no count, just a look-here cue.
  badge?: boolean
}

interface INavButton {
  icon?: IconType
  // Optional: a button that only opens a context menu drives its own toggle.
  onClick?: () => void
  caption?: string
  disabled?: boolean
  colorVariant?: IButtonProps['colorVariant']
  // Cypress handle, forwarded to the button.
  'data-cy'?: string
}

export interface ISecondaryNavProps {
  version: 'mainPage' | 'subPage' | 'form'
  title: string
  subtitle?: string
  label?: string | React.ReactNode
  breadcrumb?: IPath[]
  button?: INavButton
  secondButton?: INavButton & {
    iconSize?: number
    // When set, the button opens this context menu instead of firing onClick.
    menu?: ISection[]
  }
  user?: ICompanyMember
  formValues?: {
    total: number
    done: number
  }
  className?: string
  steps?: Array<'inactive' | 'active'>
  navigateStepBack?: () => void
  avatar?: string
  icon?: string
  backgroundImg?: string
  tabs?: INavTabs[]
}

let originalOffset: number

// A tab's `name` is an internal identifier, never translated (that is `title`),
// so it doubles as the stable handle e2e specs select on.
const tabTestId = (name: string) =>
  `nav-tab-${name.toLowerCase().replace(/\s+/g, '-')}`

const SecondaryNavigation: React.FC<ISecondaryNavProps> = ({
  title,
  subtitle,
  version,
  tabs,
  user,
  avatar,
  backgroundImg,
  icon,
  button,
  secondButton,
  label,
  breadcrumb,
  formValues,
  navigateStepBack,
  className,
  steps,
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const history = useHistory()
  const big = version === 'mainPage'
  // A tabbed subPage scrolls its header away while the tabs pin — same model as mainPage.
  // Tab-less subPages and forms keep their fixed nav.
  const scrollAway = version === 'subPage' && !!tabs

  const fixedDiv = useRef<HTMLDivElement>(null)
  const [tabsFixed, setTabsFixed] = useState<boolean>(false)
  const [tabsHeight, setTabsHeight] = useState<number>(0)

  // Open-state for the second button's context menu (when one is provided).
  // Closes on any outside click, matching the app's kebab-menu pattern.
  const [secondMenuOpen, setSecondMenuOpen] = useState<boolean>(false)
  useEffect(() => {
    const close = () => setSecondMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [])

  const handleScroll = () => {
    if (!fixedDiv.current) return
    setTabsFixed(window.scrollY - 24 >= originalOffset)
  }

  useEffect(() => {
    if (
      !window ||
      (version !== 'mainPage' && !scrollAway) ||
      !tabs ||
      !fixedDiv ||
      !fixedDiv.current
    ) {
      return
    }
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [tabs, version, fixedDiv, scrollAway])

  useEffect(() => {
    if (!fixedDiv || !fixedDiv.current || !tabs) return
    if (!tabsFixed) {
      originalOffset = fixedDiv.current.offsetTop
      // Capture the tab-bar height while in flow so the spacer can reserve it once pinned
      setTabsHeight(fixedDiv.current.offsetHeight)
    }
    // tabs?.length is in the deps because tabs can render async (e.g. after data loads):
    // without it originalOffset stays undefined and the tabs never pin.
  }, [fixedDiv, user, avatar, icon, tabsFixed, tabs?.length])

  const activeStepIndex = steps?.findIndex((step) => step === 'active')

  return (
    <Container
      className={className}
      id="secondaryNav"
      bigVersion={big}
      formVariant={version === 'form'}
      $scrollAway={scrollAway}
    >
      <InnerWrapper sub={version === 'subPage' && !tabs}>
        <StyledGridRow alignType="center">
          <StyledGridCol xs={12}>
            {breadcrumb && (
              <Paths backgroundImg={backgroundImg}>
                {breadcrumb.map((path, index) => (
                  <React.Fragment key={`breadcrumb-${index}`}>
                    {index !== 0 && breadcrumb.length > 1 && (
                      <StyledIcon
                        size={12}
                        color={
                          backgroundImg
                            ? theme.colors.lightest
                            : theme.colors.textLighter
                        }
                        type="ChevronRight"
                      />
                    )}
                    <Link
                      disableUnderline
                      color={backgroundImg ? 'lightest' : 'text72'}
                      hoverColor="primary"
                      caption={t(path.name)}
                      icon={
                        path.route && breadcrumb.length === 1
                          ? 'ChevronLeft'
                          : undefined
                      }
                      onClick={
                        path.route
                          ? () => history.push(path.route || '')
                          : undefined
                      }
                    />
                  </React.Fragment>
                ))}
              </Paths>
            )}
            {backgroundImg && big && (
              <BackgroundImg backgroundImg={backgroundImg} />
            )}
            {version === 'form' ? (
              <FormMain>
                <div>
                  {subtitle && (
                    <Text
                      color="textLight"
                      textStyle="labelSmallCaps"
                      textAlign="left"
                    >
                      {subtitle}
                    </Text>
                  )}
                  {!steps && (
                    <Text
                      textStyle="displayS"
                      textAlign="left"
                      marginBottom={formValues ? 12 : 0}
                    >
                      {title}
                    </Text>
                  )}
                  {formValues && (
                    <GoalCount
                      actions={[
                        {
                          total: formValues?.total,
                          complete: formValues?.done,
                        },
                      ]}
                    />
                  )}
                </div>

                {steps && navigateStepBack && (
                  <FormButton
                    icon="ChevronLeft"
                    caption={t('general.actions.back')}
                    hide={steps[0] === 'active'}
                    color="lightest"
                    textColor="primary"
                    hoverColor="primary"
                    hoverTextColor="lightest"
                    onClick={navigateStepBack}
                  />
                )}
                <Center>
                  {steps && (
                    <>
                      <Text textStyle="displayS" textAlign="center">
                        {title}
                      </Text>
                      <StyledStepDots
                        count={steps.length}
                        activeIndex={activeStepIndex ?? 0}
                      />
                    </>
                  )}
                </Center>
                <ButtonContainer $pinned>
                  {button && (
                    <FormButton
                      icon={button.icon}
                      caption={button.caption}
                      disabled={button.disabled}
                      colorVariant="secondary"
                      onClick={button.onClick}
                      data-cy={button['data-cy']}
                    />
                  )}
                  {label && (
                    <Text
                      tag="div"
                      color="textLight"
                      marginTop={12}
                      textStyle="caption"
                    >
                      {label}
                    </Text>
                  )}
                </ButtonContainer>
              </FormMain>
            ) : (
              <Main
                bigVersion={big}
                backgroundImg={!!backgroundImg && big}
                lessMargin={!breadcrumb}
              >
                {(user || avatar || icon) && (
                  <StyledAvatar
                    user={user}
                    imgSrc={avatar}
                    initials={icon}
                    color={
                      avatar ? 'lightest' : icon ? 'primaryLight' : 'primary'
                    }
                    size={AvatarSize.XLarge}
                  />
                )}
                <TitleBlock>
                  <StyledText
                    big={big}
                    textStyle={big ? 'displayL' : 'displayS'}
                    color={backgroundImg ? 'lightest' : 'text'}
                  >
                    {title}
                  </StyledText>
                  {user && <StyledUserTags user={user} whiteBackground />}
                </TitleBlock>
                {(button || secondButton) && (
                  <ButtonContainer $pinned={big}>
                    <RowAligned>
                      {button && (
                        <StyledButton
                          big={big}
                          icon={button.icon}
                          onClick={button.onClick}
                          disabled={button.disabled}
                          caption={button.caption}
                          colorVariant={button.colorVariant}
                          color="lightest"
                          textColor="primary"
                          hoverColor="primary"
                          hoverTextColor="lightest"
                        />
                      )}
                      {secondButton &&
                        (secondButton.menu ? (
                          <SecondMenuWrapper>
                            <StyledButton
                              big={big}
                              iconSize={secondButton.iconSize}
                              icon={secondButton.icon}
                              data-cy={secondButton['data-cy']}
                              onClick={(e) => {
                                e?.stopPropagation()
                                setSecondMenuOpen((open) => !open)
                              }}
                              disabled={secondButton.disabled}
                              caption={secondButton.caption}
                              colorVariant={secondButton.colorVariant}
                            />
                            {secondMenuOpen && (
                              <NavContextMenu
                                sections={secondButton.menu}
                                closeMenu={() => setSecondMenuOpen(false)}
                              />
                            )}
                          </SecondMenuWrapper>
                        ) : (
                          <StyledButton
                            big={big}
                            iconSize={secondButton.iconSize}
                            icon={secondButton.icon}
                            data-cy={secondButton['data-cy']}
                            onClick={secondButton.onClick}
                            disabled={secondButton.disabled}
                            caption={secondButton.caption}
                            colorVariant={secondButton.colorVariant}
                          />
                        ))}
                    </RowAligned>
                    {label && (
                      <Text
                        tag="div"
                        color="textLight"
                        textStyle="caption"
                        marginTop={16}
                      >
                        {label}
                      </Text>
                    )}
                  </ButtonContainer>
                )}
              </Main>
            )}
            {subtitle && version !== 'form' && (
              <Text color="textLight" textStyle="bodySmall">
                {subtitle}
              </Text>
            )}
            {tabs && (
              <>
                <Sticky id="secondaryNavTabs" ref={fixedDiv} fixed={tabsFixed}>
                  <InnerWrapperTabs>
                    {tabs.map((tab, index) => {
                      const active =
                        tab.active ||
                        history.location.pathname.includes(tab.path)
                      return (
                        <Tab
                          key={`nav-tab-${index}`}
                          data-cy={tabTestId(tab.name)}
                          disabled={tab.disabled}
                          active={active}
                          onClick={
                            tab.disabled
                              ? undefined
                              : () => {
                                  if (tab.onClick) {
                                    tab.onClick()
                                  } else {
                                    history.push(tab.path)
                                  }
                                }
                          }
                        >
                          {t(tab.title)}
                          {tab.badge && <NewDot />}
                        </Tab>
                      )
                    })}
                  </InnerWrapperTabs>
                </Sticky>
                {scrollAway && (
                  // Reserve the tabs' height once they pin, so the content doesn't jump up
                  <TabsSpacer height={tabsFixed ? tabsHeight : 0} />
                )}
              </>
            )}
          </StyledGridCol>
        </StyledGridRow>
      </InnerWrapper>
    </Container>
  )
}

export default SecondaryNavigation

const StyledStepDots = styled(StepDots)`
  margin-top: 12px;
`

const Container = styled.div<{
  bigVersion: boolean
  formVariant: boolean
  $scrollAway?: boolean
}>`
  background-color: ${({ theme }) => theme.colors.textLightest};
  padding-top: 24px;
  position: relative;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding-left: 16px;
  padding-right: 16px;

  ${mqFrom.M`
    padding-left: 48px;
    padding-right: 48px;
  `}

  ${mqFrom.L`
    padding-left: 64px;
    padding-right: 64px;
  `}

  ${({ bigVersion, formVariant, $scrollAway, theme }) =>
    bigVersion || $scrollAway
      ? // In the flow so the header scrolls away; only the tabs pin (see Sticky).
        // mainPage pre-reserves 424px for its big header; scrollAway uses a TabsSpacer instead.
        css`
          ${bigVersion ? mqFrom.M`height: 424px;` : mqTo.M`padding-top: 16px;`}
        `
      : css`
          position: fixed;
          top: ${formVariant ? 0 : 56}px;
          left: 0;
          width: 100vw;
          z-index: 99;
          border-bottom: ${formVariant
            ? `2px solid ${theme.colors.textLighter}`
            : 'none'};

          ${mqTo.M`
            padding-top: 16px;
          `};
        `}
`

const InnerWrapper = styled.div<{ sub: boolean }>`
  margin: 0 auto;
  width: 100%;
  height: 100%;
  position: relative;
  ${({ sub, theme }) =>
    sub &&
    css`
      padding-bottom: 24px;
      border-bottom: 2px solid ${theme.colors.textLighter};
    `}

  ${mqFrom.L`
    max-width: 1184px;
  `}
`

const StyledGridRow = styled(GridRow)`
  height: 100%;
`

const StyledGridCol = styled(GridCol)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledAvatar = styled(Avatar)`
  border: 4px solid ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthThree};
  margin-bottom: 24px;
  flex-shrink: 0;

  ${mqFrom.M`
    margin: 0 48px 0 0;
  `}
`

const StyledUserTags = styled(UserTags)`
  margin-top: 12px;
  justify-content: center;

  ${mqFrom.M`
    justify-content: flex-start;
  `}

  ${mqFrom.L`
    flex-wrap: nowrap;
  `}
`

const FormMain = styled(Row)`
  margin-bottom: 16px;

  ${mqFrom.M`
    margin-bottom: 24px;
  `}
`

const Center = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;

  ${mqFrom.M`
    align-items: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `}
`

const ButtonContainer = styled.div<{ $pinned?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
  flex-shrink: 0;

  /* mainPage and form headers stack their content on a phone, so their actions
     are pinned to the top-right corner. A subPage header is a row: there the
     actions stay in flow beside the title, clear of the absolute breadcrumb. */
  ${({ $pinned }) =>
    $pinned &&
    css`
      ${mqTo.M`
        position: absolute;
        right: 0px;
        top: 0px;
      `}
    `}
`

const FormButton = styled(Button)<{ hide?: boolean }>`
  width: fit-content;

  ${({ hide }) =>
    hide &&
    `
    opacity: 0;
  `}

  ${mqTo.M`
    > span {
      margin-right: 0;
    }

     > label {
      ${({ icon, caption }) =>
        icon && caption ? 'display: none;' : 'display: inline-block;'}
    }
  `}
`

const BackgroundImg = styled.div<{ backgroundImg: string }>`
  ${({ backgroundImg }) =>
    backgroundImg &&
    `
      width: 100%;
      border-radius: 24px;
      position: absolute;
      top: 0;
      bottom: 174px;
      left: 0;
      background-image: url('${backgroundImg}');
      background-position: center;
      background-size: cover;
    `}

  ${mqFrom.M`
      bottom: 88px;
    `}
`

const Main = styled.div<{
  bigVersion: boolean
  lessMargin?: boolean
  backgroundImg?: boolean
}>`
  display: flex;
  align-items: center;
  flex-direction: ${({ bigVersion }) => (bigVersion ? 'column' : 'row')};
  margin: ${({ bigVersion, lessMargin, backgroundImg }) =>
    backgroundImg
      ? '180px 0 16px'
      : bigVersion
      ? '64px 0 48px'
      : lessMargin
      ? '8px 0'
      : '24px 0 8px'};

  ${mqFrom.M`
    flex-direction: row;
    margin: ${({
      bigVersion,
      lessMargin,
      backgroundImg,
    }: {
      bigVersion: boolean
      lessMargin?: boolean
      backgroundImg?: boolean
    }) =>
      backgroundImg
        ? '184px 32px 0px 48px'
        : bigVersion
        ? '96px 0 88px'
        : lessMargin
        ? '8px 0'
        : '28px 0 8px'};
  `}
`

const StyledText = styled(Text)<{ big: boolean }>`
  ${({ big }) =>
    big &&
    css`
      ${mqTo.M`
        text-align: center;
      `}
    `}

  ${mqTo.M`
    color: ${({ theme }) => theme.colors.text};
  `}
`

const StyledButton = styled(Button)<{ big: boolean }>`
  z-index: 1;
  ${({ big }) =>
    big
      ? css`
          margin-top: 8px;
          ${mqFrom.M`
      margin: 0 0 0 auto;
    `}
        `
      : css`
          margin-left: auto;

          &:nth-of-type(2) {
            margin-left: 8px;
          }
        `}

  /* A phone only has room for the icon. The caption is clipped rather than
     removed, so the button keeps its accessible name; the icon's 8px gap is
     reset because the label still counts as the last child. */
  ${mqTo.S`
    ${({ icon, caption }: IButtonProps) =>
      icon && caption
        ? `
          > span {
            margin-right: 0;
          }

          > label {
            position: absolute;
            width: 1px;
            height: 1px;
            margin: -1px;
            padding: 0;
            overflow: hidden;
            clip-path: inset(50%);
            white-space: nowrap;
          }
        `
        : ''}
  `}
`

// The nav's ••• menu hangs off a button-sized anchor, where ContextMenu's
// mobile "width: 100%" would clip its own rows. Size it to its content instead,
// capped to the viewport so it can never run off the right edge.
const NavContextMenu = styled(ContextMenu)`
  width: max-content;
  max-width: calc(100vw - 32px);
`

const TitleBlock = styled.div`
  z-index: 1;
  min-width: 0;
`

// Relative anchor for a second button that drops a ContextMenu. Carries the 8px
// gap itself, since the wrapped button no longer matches StyledButton's
// :nth-of-type(2) spacing rule above.
const SecondMenuWrapper = styled.div`
  position: relative;
  display: flex;
  margin-left: 8px;
`

const Paths = styled.div<{ backgroundImg?: string }>`
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 1;

  ${({ backgroundImg }) =>
    backgroundImg &&
    css`
      top: 24px;
      left: 24px;
    `}
`

const StyledIcon = styled(Icon)`
  margin: 0 8px;
`

const Sticky = styled.div<{ fixed: boolean }>`
  margin-top: auto;
  top: 56px;
  position: ${({ fixed }) => (fixed ? 'fixed' : 'initial')};
  left: 0;
  background: ${({ theme }) => theme.colors.textLightest};
  z-index: 99;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  width: 100vw;
  padding-left: 16px;
  padding-right: 16px;
  overflow-x: scroll;
  display: flex;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }

  ${mqFrom.M`
    padding-left: 48px;
    padding-right: 48px;
  `}

  ${mqFrom.L`
    padding-left: 64px;
    padding-right: 64px;
  `}
`

const TabsSpacer = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
`

const InnerWrapperTabs = styled.ul`
  margin: 0;
  display: inline-flex;
  align-items: center;
  list-style: none;
  padding: 0px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.textLighter};

  ${mqFrom.M`
    width: 100%;
    margin: 0 auto;
  `}

  ${mqFrom.L`
    max-width: 1184px;
  `}
`

const Tab = styled.li<{ active: boolean; disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
  margin-right: 24px;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  ${applyTextStyle('subtitle')};
  letter-spacing: 0.5px;
  color: ${({ active, theme, disabled }) =>
    disabled
      ? theme.colors.textLighter
      : !active
      ? theme.colors.textLight
      : theme.colors.primary};
  border-bottom: 2px solid
    ${({ active, theme }) =>
      active ? theme.colors.primary : theme.colors.textLighter};
  margin-bottom: -2px;

  &:hover {
    color: ${({ theme, disabled }) =>
      disabled ? theme.colors.textLighter : theme.colors.primary};
  }

  ${mqFrom.M`
    margin-right: 32px;
    padding: 16px 0;
  `}
`

// A small red "look here" dot, also used next to names in PeopleRow.
export const NewDot = styled.div`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
`
